import { useId, useState } from 'react'

import { faChevronDown } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled } from 'styled-components'

import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

import type { SaleKind } from '@/buyer-portal/components/features/listing/actions/auction/BidFormProvider/BidConfirmationModal/sellerCollectsBidCopy'

const ns = 'Listing.actionColumn.payment.howYoullPay'

const Panel = styled.section`
  background: ${Colors.Semantic.Neutral.white};
  border: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.5rem;
  padding: 1.5rem;
`

const Header = styled.button`
  align-items: center;
  background: none;
  border: 0;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: 0;
  text-align: left;
  width: 100%;
`

const Title = styled.h3`
  ${Typography.Subtitle1}
  margin: 0;
`

const Chevron = styled(FontAwesomeIcon)<{ $isExpanded: boolean }>`
  color: ${Colors.Semantic.Neutral.dark_grey};
  font-size: 0.875rem;
  transform: rotate(${({ $isExpanded }) => ($isExpanded ? '180deg' : '0deg')});
  transition: transform 0.15s ease;
`

const Steps = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  list-style: none;
  margin: 1rem 0 0;
  padding: 0;
`

const Step = styled.li`
  display: grid;
  gap: 0.75rem;
  grid-template-columns: auto 1fr;
`

// The numbered badge the design leads each track with. Light blue, square-ish
// rather than a circle, and sized so a two-digit number would still fit.
const Ordinal = styled.span`
  ${Typography.Subtitle3}
  align-items: center;
  /* Square and pinned to the first line: a grid item would otherwise stretch
     to the height of the two lines beside it. */
  align-self: start;
  background: ${Colors.Semantic.SpecificFunctions.Tags.info};
  border-radius: 0.25rem;
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  display: flex;
  height: 1.5rem;
  justify-content: center;
  width: 1.5rem;
`

const StepTitle = styled.p`
  ${Typography.Subtitle2}
  margin: 0 0 0.25rem;
`

const Detail = styled.p`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
  margin: 0 0 0.25rem;

  &:last-child {
    margin-bottom: 0;
  }
`

type HowYoullPayProps = {
  /** Falls back to generic copy when the seller cannot be named. */
  sellerName?: string
  /**
   * Derived from the seller's subscription, never from the config. Undefined
   * while it is still loading — neither fee state is claimed until it is known.
   */
  hasBuyerPremium?: boolean
  /** Folds the freight into the one payment a fee-free storefront asks for. */
  bstockBooksFreight?: boolean
  /** From the storefront; the deadline is dropped when it says nothing. */
  paymentDueBusinessDays?: number
  /** The deadline counts from the win, which a Buy Now buyer never has. */
  saleKind?: SaleKind
  /**
   * The seller is paid by wire and nothing else, with no invoice of its own
   * to say so, so the seller track names the method. Worked out by the caller,
   * which holds the storefront config this card never sees.
   */
  paidByWireOnly?: boolean
}

/**
 * Who the buyer pays on a Seller Collects listing, and for what. Replaces the
 * B-Stock payment-method chips, which describe methods that cannot be used
 * here and previously offered B-Stock's own wire details for money owed to
 * the seller.
 *
 * Two numbered tracks of two lines each, collapsible, matching the design.
 * Accepted methods, the payment deadline and the seller's contacts are
 * deliberately absent: the PRD routes that content to the confirmation email
 * and the order details page, and this card is the pre-bid summary. The one
 * exception is a storefront paid by wire alone that sends no invoice of its
 * own, where the seller track says "paid by wire transfer" as the design does.
 *
 * A fee-free storefront keeps the card and shows a single bulleted track:
 * LDP-3 still answers "who do I pay" there, and the one payment covers the
 * freight too where B-Stock books it.
 */
export const HowYoullPay = ({
  sellerName,
  hasBuyerPremium,
  bstockBooksFreight = false,
  paymentDueBusinessDays,
  saleKind = 'bid',
  paidByWireOnly = false,
}: HowYoullPayProps) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const stepsId = useId()
  // The storefront name, not `sellerLegalName`: one seller is introduced one
  // way across the listing page and both confirmation modals.
  const displayName = sellerName

  // Only an explicit `false` is a fee-free storefront. While it resolves the
  // card still shows what is already known — who the buyer pays and how.
  const isFeeFree = hasBuyerPremium === false

  return (
    <Panel data-testid="how-youll-pay">
      <Header
        type="button"
        aria-expanded={isExpanded}
        aria-controls={stepsId}
        onClick={() => setIsExpanded((expanded) => !expanded)}
      >
        <Title>
          <FormattedMessage id={`${ns}.title`} />
        </Title>
        <Chevron
          icon={faChevronDown}
          $isExpanded={isExpanded}
          aria-hidden="true"
        />
      </Header>

      {isExpanded && (
        <Steps id={stepsId}>
          <Step data-testid="how-youll-pay-seller">
            <Ordinal aria-hidden="true">{isFeeFree ? '\u2022' : '1'}</Ordinal>
            <div>
              <StepTitle>
                {displayName ? (
                  <FormattedMessage
                    id={`${ns}.seller.title`}
                    values={{ sellerName: displayName }}
                  />
                ) : (
                  <FormattedMessage id={`${ns}.seller.titleFallback`} />
                )}
              </StepTitle>

              <Detail>
                <FormattedMessage
                  id={`${ns}.seller.${
                    isFeeFree
                      ? `${saleKind === 'purchase' ? 'purchase.' : ''}feeFreeDetail`
                      : 'forTheGoods'
                  }`}
                  values={{
                    covers: bstockBooksFreight ? 'goodsAndShipping' : 'goods',
                    method: paidByWireOnly ? 'wire' : 'other',
                    // Whether there is a deadline at all is its own answer: a
                    // storefront that genuinely allows zero business days must
                    // not read as one that published nothing.
                    hasDeadline:
                      paymentDueBusinessDays === undefined ? 'no' : 'yes',
                    days: paymentDueBusinessDays ?? 0,
                  }}
                />
              </Detail>
            </div>
          </Step>

          {/* Nothing until the subscription resolves: claiming a fee and then
              correcting it is worse than saying nothing for a moment. A
              fee-free storefront has no second track at all. */}
          {hasBuyerPremium === undefined || isFeeFree ? null : (
            <Step data-testid="how-youll-pay-bstock">
              <Ordinal aria-hidden="true">2</Ordinal>
              <div>
                <StepTitle>
                  <FormattedMessage id={`${ns}.bstock.title`} />
                </StepTitle>
                <Detail>
                  <FormattedMessage id={`${ns}.bstock.desc`} />
                </Detail>
              </div>
            </Step>
          )}
        </Steps>
      )}
    </Panel>
  )
}
